import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Popper } from '@mui/material';
import { Instance } from '@popperjs/core/lib';
import { createPortal } from 'react-dom';

import useAlertRects from './useAlertRects';
import { useAlertPopoverPosition } from '../../../hooks/useAlertPopoverPosition';
import useHoverBridge from '../../../hooks/useHoverBridge';
import { GrammarCheckAlert } from '../../../types';
import getUnderlineColor from '../../../utils/getUnderlineColor';
import AlertPopover from '../../AlertPopover';
import { HighlighterProps } from '../types';

const getHoveredRectColor = (alert: GrammarCheckAlert): string => {
  if (
    alert?.category?.toLowerCase() === 'typography' ||
    alert?.category?.toLowerCase() === 'punctuation'
  ) {
    return 'rgba(208,28,208,0.1)';
  }
  return 'rgba(240, 68, 56, 0.1)';
};

const underline = (
  context: CanvasRenderingContext2D,
  top: number,
  left: number,
  width: number,
  underlineColor: string,
): void => {
  context.beginPath();
  context.moveTo(left, top);
  context.lineTo(left + width, top);
  context.strokeStyle = underlineColor;
  context.stroke();
};

const fillRect = (
  context: CanvasRenderingContext2D,
  left: number,
  top: number,
  width: number,
  height: number,
  selectedColor: string,
) => {
  context.fillStyle = selectedColor;
  context.fillRect(left, top, width, height);
};

const POPOVER_HIDE_DELAY_MS = 150;

const Highlighter: React.FC<HighlighterProps> = ({
  checkableElement,
  targetEl,
  alerts,
  onInsert,
  onDismiss,
  onPopoverShown,
}) => {
  const popperRef = useRef<Instance>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const rectsRef = useRef<DOMRect[][] | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { rects, hoveredRectIndex } = useAlertRects(
    checkableElement,
    targetEl,
    container,
    alerts,
  );

  const handlePopoverShown = useCallback(
    (index: { alertIndex: number; rectIndex: number }) => {
      if (alerts[index.alertIndex]) {
        onPopoverShown?.(alerts[index.alertIndex]);
      }
    },
    [alerts, onPopoverShown],
  );

  const [activeAlertIndex, popoverHoverHandlers, resetPopover] = useHoverBridge(
    hoveredRectIndex,
    POPOVER_HIDE_DELAY_MS,
    handlePopoverShown,
  );

  const updateCanvas = () => {
    setTimeout(() => {
      setupCanvas();
      updateCanvasHighlights();
      popperRef.current?.update();
    });
  };

  const getReferenceElement = useCallback(() => {
    if (container) {
      return container;
    }
    return targetEl;
  }, [container, targetEl]);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = targetEl.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height + 20;

    canvas.style.top = `-${containerRef.current?.scrollTop || 0}px`;
  };

  const updateCanvasHighlights = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');

    if (context) {
      context.lineWidth = 2;
      context.clearRect(0, 0, canvas.width, canvas.height);

      const currentRects = rectsRef.current;

      if (currentRects) {
        currentRects.forEach((alertRects, i) => {
          alertRects.forEach((alertRect) => {
            underline(
              context,
              alertRect.top + alertRect.height,
              alertRect.left,
              alertRect.width,
              getUnderlineColor(alerts[i]),
            );
          });
        });

        if (
          activeAlertIndex !== null &&
          currentRects[activeAlertIndex.alertIndex]
        ) {
          const hoveredRects = currentRects[activeAlertIndex.alertIndex];
          hoveredRects.forEach((hoveredRect) => {
            fillRect(
              context,
              hoveredRect.left,
              hoveredRect.top,
              hoveredRect.width,
              hoveredRect.height,
              getHoveredRectColor(alerts[activeAlertIndex.alertIndex]),
            );
          });
        }
      }
    }
  };

  useEffect(() => {
    if (rects) {
      rectsRef.current = rects;
    }

    if (targetEl && canvasRef.current) {
      updateCanvas();
    }
  }, [targetEl, rects, activeAlertIndex]);

  useEffect(() => {
    window.addEventListener('resize', updateCanvas);

    return () => {
      window.removeEventListener('resize', updateCanvas);
    };
  }, []);

  useEffect(() => {
    if (targetEl && canvasRef.current) {
      const highlighterContainer = checkableElement.getHighlighterContainer();

      if (containerRef.current !== highlighterContainer) {
        setContainer(highlighterContainer);
        containerRef.current = highlighterContainer;
      }

      checkableElement.element.addEventListener('focusin', updateCanvas);
      highlighterContainer?.addEventListener('scroll', updateCanvas);

      return () => {
        checkableElement.element.removeEventListener('focusin', updateCanvas);
        highlighterContainer?.removeEventListener('scroll', updateCanvas);
      };
    }
  }, [targetEl, checkableElement, alerts]);

  const referenceElement = getReferenceElement();

  const { popoverRef, positionStyle } = useAlertPopoverPosition({
    targetEl: referenceElement,
    alertRect:
      activeAlertIndex !== null && rects[activeAlertIndex.alertIndex]
        ? rects[activeAlertIndex.alertIndex][activeAlertIndex.rectIndex]
        : undefined,
    containerEl: container,
  });

  return referenceElement ? (
    <>
      <Popper
        disablePortal
        popperRef={popperRef}
        anchorEl={referenceElement}
        popperOptions={{
          strategy: 'fixed',
        }}
        placement="top-start"
        open
        modifiers={[
          {
            name: 'flip',
            options: {
              fallbackPlacements: [],
            },
          },
        ]}
      >
        <div
          style={{
            position: 'absolute',
            width: referenceElement.clientWidth,
            height: referenceElement.clientHeight,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', pointerEvents: 'none' }}
          />
        </div>
      </Popper>

      {activeAlertIndex !== null &&
        alerts[activeAlertIndex.alertIndex] &&
        rects[activeAlertIndex.alertIndex] &&
        createPortal(
          <div {...popoverHoverHandlers}>
            <AlertPopover
              alert={alerts[activeAlertIndex.alertIndex]}
              alertRect={
                rects[activeAlertIndex.alertIndex][activeAlertIndex.rectIndex]
              }
              onInsert={(replacement): void => {
                resetPopover();
                onInsert?.(alerts[activeAlertIndex.alertIndex], replacement);
              }}
              onDismiss={(): void => {
                resetPopover();
                onDismiss?.(alerts[activeAlertIndex.alertIndex]);
              }}
              targetEl={referenceElement}
              containerEl={container}
              popoverProps={{
                ref: popoverRef,
                style: positionStyle,
              }}
            />
          </div>,
          document.body,
        )}
    </>
  ) : null;
};

export default Highlighter;
